import {
  closePlanogramProductPopover,
  pulsePlanogramProduct,
  renderCoverageBetaPlanogramProduct,
  useIsPlanogramPopoverOpen,
} from "@/components/coverage-beta/coverage-beta-right-panel/coverage-beta-planogram-view/CoverageBetaPlanogramProduct";
import { PlanogramSearchModal } from "@/components/coverage/PlanogramSearchModal";
import { COVERAGE_BETA_HOTKEYS } from "@/hooks/useCoverageBetaHotkeys";
import {
  selectActiveAreaIdx,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import { cn } from "@/utils/utils";
import { OoSParcelSchema } from "@augmodo/lebron-browser-client";
import {
  PlanogramViewer as ReusablePlanogramViewer,
  type PlanogramViewerHandle,
  type PlanogramZoomHotkeys,
} from "@augmodo/reusable-components";
import { mvpApiGetProductOptions } from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import { useAtlasPlanogram } from "@labeling-tools/api/hooks";
import { PlanogramViewer } from "@labeling-tools/ui-components/src/planogram-viewer/planogram-viewer.comp";
import { useActivePlanoProductInfoStore } from "@labeling-tools/ui-components/src/planogram-viewer/use-active-plano-product-info-store";
import { Button, Center, Loader, Text } from "@mantine/core";
import { IconMapPinOff, IconPlus, IconReplace } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGate } from "statsig-react";

interface Props {
  parcelResp: OoSParcelSchema | undefined;
  /** True while the planogram tab is the visible right-panel view. */
  isActiveView: boolean;
  /**
   * True once the task's blocking data — the query image AND the active
   * reference (shelf) image — have both painted. Until then the planogram
   * queries, and the ~50 per-product pack-shot image downloads a resolved
   * planogram triggers, stay off, so they can't compete with those two images
   * for bandwidth. Ignored while this tab is actually the visible view (see
   * `isPlanogramReady` below): a labeler who switches tabs early still gets
   * their planogram, with the existing progressive load (background image
   * first, product pack-shots layered on as they arrive) rather than a stall.
   */
  isWarmupReady: boolean;
}

// We fetch a fixed number of planograms to respect the rules of hooks, so the
// number of aisles/slots is capped.
const MAX_PLANOGRAM_SLOTS = 4;

/** The JSON binding's primary key for an action ("" if unbound). */
const primaryKeyFor = (action: string): string =>
  COVERAGE_BETA_HOTKEYS.find((b) => b.action === action)?.keys[0] ?? "";

/**
 * Whether `productId` has a facing among `products`. The one matcher behind
 * both the "which tab is it on" lookup and the zoom guards below, so all three
 * agree on what "this product is on this planogram" means.
 */
const isProductOnPlanogram = (
  products: { product_id?: string | null; id?: string | null }[],
  productId: string,
): boolean =>
  !!productId &&
  products.some((p) => p.product_id === productId || p.id === productId);

// The reusable viewer's zoom/reset keys, sourced from the same
// config/coverage-beta-hotkeys.json as the app-wide hotkeys so a rebind there
// carries over. The viewer matches case-insensitively, so the JSON's uppercase
// variants (["a", "A"], …) are covered by the primary key alone. Module-level:
// referentially stable, same as the other reusable-viewer props.
const REUSABLE_PLANO_ZOOM_HOTKEYS: PlanogramZoomHotkeys = {
  zoomIn: primaryKeyFor("zoom_in"),
  zoomOut: primaryKeyFor("zoom_out"),
  resetView: primaryKeyFor("reset_view"),
};

export const CoverageBetaPlanogramView = (props: Props) => {
  const { parcelResp, isActiveView, isWarmupReady } = props;

  // Fires the planogram queries once warm-up completes, or immediately if the
  // labeler jumps to this tab before that (see the `isWarmupReady` prop doc).
  const isPlanogramReady = isWarmupReady || isActiveView;

  // Trial of the standalone viewer from @augmodo/reusable-components. Both
  // viewers report product clicks through the same
  // useActivePlanoProductInfoStore bridge, so the selection logic below is
  // shared; the gate only swaps the rendering.
  const { value: isEnableReusablePlanoViewer } = useGate(
    "fe_enable_reusable_plano_viewer_coverage_beta",
  );

  const basePlanogramIds = useCoverageBetaStore((s) => s.basePlanogramIds);
  const overridePlanogramIds = useCoverageBetaStore(
    (s) => s.overridePlanogramIds,
  );
  const extraPlanogramIds = useCoverageBetaStore((s) => s.extraPlanogramIds);
  const activePlanogramTab = useCoverageBetaStore((s) => s.activePlanogramTab);
  const setActivePlanogramTab = useCoverageBetaStore(
    (s) => s.setActivePlanogramTab,
  );
  const applyPlanogramModalEdits = useCoverageBetaStore(
    (s) => s.applyPlanogramModalEdits,
  );
  // Which empty area the planogram (and the shelf-images view, and
  // reference-detection "selected" styling) should treat as active. In QA
  // mode this follows the pager, not the frozen draw/edit-flow index — see
  // selectActiveAreaIdx's doc comment.
  const activeAreaIdx = useCoverageBetaStore(selectActiveAreaIdx);
  const isReadOnly = useCoverageBetaStore((s) => s.isReadOnly);

  // Highlight every product selected for the active area on the planogram,
  // regardless of how it was picked (reference or planogram) — the viewer
  // matches by product_id.
  const selectedProductIds = useCoverageBetaStore((s) => {
    const area = s.idxToFeEmptyArea[selectActiveAreaIdx(s)];
    if (!area) return "";
    return [...new Set(area.selectedProducts.map((p) => p.id || ""))]
      .filter(Boolean)
      .join(",");
  });
  const selectedProductIdsList = useMemo(
    () => (selectedProductIds ? selectedProductIds.split(",") : []),
    [selectedProductIds],
  );

  // The single product to frame/ping when the planogram opens: the most
  // recently selected one for the active area. Framing just this one (rather
  // than every selected id) avoids the viewer centering on the empty "average"
  // midpoint between several spread-out picks.
  const focusProductId = useCoverageBetaStore((s) => {
    const area = s.idxToFeEmptyArea[selectActiveAreaIdx(s)];
    if (!area || area.selectedProducts.length === 0) return "";
    return area.selectedProducts[area.selectedProducts.length - 1].id || "";
  });

  // Hovering a product on the query image (or a reference detection) sets
  // activeProductId; surface it as a transient teal highlight on the matching
  // planogram box — a "you are here" cue that never pans the view.
  const activeProductId = useCoverageBetaStore((s) => s.activeProductId);
  // Also highlight the detections neighbouring the hovered empty area, so the
  // same products light up on the planogram as on the reference image.
  const neighbourProductIds = useCoverageBetaStore(
    (s) => s.neighbourProductIds,
  );
  const highlightedProductIds = useMemo(
    () => [
      ...(activeProductId ? [activeProductId] : []),
      ...neighbourProductIds,
    ],
    [activeProductId, neighbourProductIds],
  );
  // Kept referentially stable on purpose: a fresh array every render would
  // make the reusable viewer remerge its placed list (and break its per-cell
  // memos' item comparison) on renders where nothing changed. The `children`
  // render prop has the same reference-identity requirement — it lives as a
  // module-level constant in CoverageBetaPlanogramProduct.
  const reusableActiveProductIds = useMemo(
    // Selected first, hover-highlights after — actives paint last, so hovered
    // products end up on top.
    () => [...selectedProductIdsList, ...highlightedProductIds],
    [selectedProductIdsList, highlightedProductIds],
  );

  const setHoveredView = useCoverageBetaStore((s) => s.setHoveredView);
  const setLocatedProductId = useCoverageBetaStore(
    (s) => s.setLocatedProductId,
  );
  const setPlanogramPinchRef = useCoverageBetaStore(
    (s) => s.setPlanogramPinchRef,
  );
  const planogramResetNonce = useCoverageBetaStore(
    (s) => s.planogramResetNonce,
  );

  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPreselectedSlot, setModalPreselectedSlot] = useState<
    number | null
  >(null);

  const planogramSlots = useMemo(() => {
    const slots = basePlanogramIds.map((baseId, i) => ({
      aisleIndex: i,
      planogramId: overridePlanogramIds[i] ?? baseId,
    }));
    extraPlanogramIds.forEach((id, i) => {
      slots.push({ aisleIndex: basePlanogramIds.length + i, planogramId: id });
    });
    return slots;
  }, [basePlanogramIds, overridePlanogramIds, extraPlanogramIds]);

  // Fixed number of planogram fetches (rules of hooks).
  const planoOptions = { enabled: isPlanogramReady };
  const plano0 = useAtlasPlanogram(
    planogramSlots[0]?.planogramId,
    planoOptions,
  );
  const plano1 = useAtlasPlanogram(
    planogramSlots[1]?.planogramId,
    planoOptions,
  );
  const plano2 = useAtlasPlanogram(
    planogramSlots[2]?.planogramId,
    planoOptions,
  );
  const plano3 = useAtlasPlanogram(
    planogramSlots[3]?.planogramId,
    planoOptions,
  );
  const planoQueries = useMemo(
    () => [plano0, plano1, plano2, plano3].slice(0, planogramSlots.length),
    [plano0, plano1, plano2, plano3, planogramSlots.length],
  );

  const activeSlot = planogramSlots[activePlanogramTab];
  const activePlanogramId = activeSlot?.planogramId ?? "";
  const activePlanogramData = planoQueries[activePlanogramTab]?.data;
  const activePlanogramLoading =
    planoQueries[activePlanogramTab]?.isLoading ?? false;

  const slotLabels = useMemo(
    () =>
      planogramSlots.map((slot, i) => ({
        aisleIndex: i,
        planogramId: slot.planogramId,
        label: planoQueries[i]?.data?.description ?? `Planogram ${i + 1}`,
      })),
    [planogramSlots, planoQueries],
  );

  // Which loaded planogram tab (if any) contains the focused product. Lets us
  // tell the labeler "not on this planogram" — and point at the right tab when
  // the product actually lives on another aisle — instead of the viewer just
  // silently recentering on the whole planogram. -1 = not on any loaded tab.
  const focusProductSlot = useMemo(() => {
    if (!focusProductId) return -1;
    for (let i = 0; i < planoQueries.length; i++) {
      const products = planoQueries[i]?.data?.products ?? [];
      if (isProductOnPlanogram(products, focusProductId)) return i;
    }
    return -1;
    // planoQueries identities are stable per-render via the earlier useMemo.
  }, [focusProductId, planoQueries]);

  const isFocusProductPlacedHere = useMemo(
    () =>
      isProductOnPlanogram(activePlanogramData?.products ?? [], focusProductId),
    [activePlanogramData, focusProductId],
  );

  // Surface a notice whenever the focused product isn't visible on the CURRENT
  // tab — either it's on another loaded aisle (offer a jump) or it isn't on any
  // loaded planogram (just say so). Gated on the active planogram having loaded
  // so we don't flash "not found" mid-fetch.
  const focusProductOnOtherTab =
    focusProductSlot !== -1 && focusProductSlot !== activePlanogramTab;
  const showFocusNotice =
    !!focusProductId &&
    !activePlanogramLoading &&
    focusProductSlot !== activePlanogramTab;

  // Scope the planogram search to the task's store (parcel.store.atlas_id),
  // never planogram.store_ids — that's the template's chain-wide deployment list.
  const retailerId = activePlanogramData?.retailer?.id ?? null;
  const storeId =
    (parcelResp?.store as { atlas_id?: string } | undefined)?.atlas_id ?? null;

  // Keep live refs for the async click bridge (avoids stale closures / deps).
  const activePlanogramIdRef = useRef(activePlanogramId);
  activePlanogramIdRef.current = activePlanogramId;
  const referencesRef = useRef(parcelResp?.references);
  referencesRef.current = parcelResp?.references;

  // Auto-switch the tab to the active empty area's aisle when it changes.
  // -1 (QA pager on a person item) intentionally leaves the tab alone —
  // there's no area to switch to.
  useEffect(() => {
    if (activeAreaIdx < 0) return;
    const { boxIdxToSlot, setActivePlanogramTab: setTab } =
      useCoverageBetaStore.getState();
    setTab(boxIdxToSlot[activeAreaIdx] ?? 0);
  }, [activeAreaIdx]);

  // The viewer auto-pans to its zoom target whenever that target changes. If we
  // let it track the live selection, every product click would re-center the
  // planogram under the cursor (a painful jump). Instead snapshot the selection
  // only when the area/tab/planogram changes, so entering a context centers on
  // its products but clicking within it leaves the view put.
  const [zoomToProductIds, setZoomToProductIds] = useState<string[]>([]);
  // Camera handle for the reusable viewer (gate on). Null whenever that viewer
  // isn't mounted — gate off, or the active planogram still loading — so every
  // `?.` call below silently no-ops in those states and needs no gate check.
  const reusableViewerRef = useRef<PlanogramViewerHandle>(null);
  useEffect(() => {
    const ids = focusProductId ? [focusProductId] : [];
    setZoomToProductIds(ids);
    // The reusable viewer takes camera moves as calls, not props ([] is a
    // no-op). activePlanogramData is a dep for its sake: the viewer unmounts
    // while a planogram loads, so a call fired on a tab switch mid-fetch hits a
    // null ref — data arriving re-fires the effect once the viewer is back.
    // The legacy branch is unaffected (it dedupes zoom targets by content).
    //
    // Only ever hand it an id that's actually placed on the shown planogram —
    // see isFocusProductPlacedHere for why.
    reusableViewerRef.current?.zoomToProducts(
      isFocusProductPlacedHere ? ids : [],
    );
    // Ping the framed product (reusable cells subscribe to the pulse; a
    // product with no cell on the shown planogram simply has nothing to
    // pulse, which is the legacy "only when actually framed" rule for free).
    if (focusProductId) {
      pulsePlanogramProduct(focusProductId);
    }
  }, [
    activeAreaIdx,
    activePlanogramTab,
    activePlanogramId,
    activePlanogramData,
  ]);

  // Re-frame on the current selection each time the planogram becomes the
  // visible view. The common flow is: pick a product on the reference image,
  // then switch here to see where it sits — but a plain selection change is
  // deliberately NOT a re-snapshot trigger (above), so without this the view
  // stays wherever it was and the labeler has to hunt for the red box. A
  // just-picked product changes the id set, so entering the view centers on it;
  // firing only on view-entry (not per click) keeps clicks within the planogram
  // from yanking the view around.
  const wasActiveViewRef = useRef(isActiveView);
  useEffect(() => {
    if (isActiveView && !wasActiveViewRef.current) {
      // The legacy viewer re-checks the match itself, so it always gets the ids.
      setZoomToProductIds(focusProductId ? [focusProductId] : []);
      if (focusProductId) {
        // Same placement guard as the snapshot effect above: this is arguably
        // the *more* likely path to arm the stale ref, since the documented
        // flow is "pick on the reference image, then switch here to see where
        // it sits" — and in QA that product often has no facing on this aisle.
        if (isFocusProductPlacedHere) {
          reusableViewerRef.current?.zoomToProducts([focusProductId]);
        }
        // Pulsing is safe unguarded: a product with no cell here has nothing
        // to pulse (same rule as the snapshot effect).
        pulsePlanogramProduct(focusProductId);
      }
    }
    wasActiveViewRef.current = isActiveView;
  }, [isActiveView, focusProductId, isFocusProductPlacedHere]);

  // A product box clicked on the query image or a context frame asks to be
  // framed here. Only while the planogram is the shown view — otherwise the
  // request is dropped (the hover highlight already covers it). A product on
  // another loaded aisle switches to that tab first; this effect re-runs once
  // the tab's data is current and frames it there.
  const planogramFocusRequest = useCoverageBetaStore(
    (s) => s.planogramFocusRequest,
  );
  const clearPlanogramFocusRequest = useCoverageBetaStore(
    (s) => s.clearPlanogramFocusRequest,
  );
  useEffect(() => {
    if (!planogramFocusRequest) return;
    if (!isActiveView) {
      clearPlanogramFocusRequest();
      return;
    }
    if (activePlanogramLoading) return;
    const { productId } = planogramFocusRequest;
    if (isProductOnPlanogram(activePlanogramData?.products ?? [], productId)) {
      setZoomToProductIds([productId]);
      reusableViewerRef.current?.zoomToProducts([productId]);
      pulsePlanogramProduct(productId);
      clearPlanogramFocusRequest();
      return;
    }
    const slot = planoQueries.findIndex((q) =>
      isProductOnPlanogram(q?.data?.products ?? [], productId),
    );
    if (slot !== -1 && slot !== activePlanogramTab) {
      setActivePlanogramTab(slot);
      return;
    }
    // Not on any loaded planogram — nothing to frame.
    clearPlanogramFocusRequest();
  }, [
    planogramFocusRequest,
    isActiveView,
    activePlanogramLoading,
    activePlanogramData,
    activePlanogramTab,
    planoQueries,
    setActivePlanogramTab,
    clearPlanogramFocusRequest,
  ]);

  // Drop the query image's locate highlight when the panel leaves this view.
  // Both right-panel views stay mounted (the inactive one is `invisible
  // pointer-events-none`, so the planogram keeps its pan/zoom), which means a
  // tab switch mid-hover can hide the cell without ever firing its mouseleave —
  // leaving a teal box on the shelf photo that nothing is pointing at any more.
  useEffect(() => {
    if (!isActiveView) setLocatedProductId("");
  }, [isActiveView, setLocatedProductId]);

  // Reset-view hotkey → reusable viewer. The store nonce stays (the legacy
  // viewer listens to it as a prop); this adapter turns each bump into the
  // handle call. Nonce 0 is the initial store state, not a reset request.
  useEffect(() => {
    if (planogramResetNonce === 0) return;
    reusableViewerRef.current?.resetView();
  }, [planogramResetNonce]);

  // Admin details popover (reusable viewer, read-only view). While one is
  // open, freeze wheel-zoom — the popover is anchored to a box that zooming
  // would slide away from under it. The built-in popover does this itself; a
  // custom one must (see the reusable viewer's `enableZoom` docs). Always
  // false in the labeler view, where no popover ever opens.
  const isPlanoPopoverOpen = useIsPlanogramPopoverOpen();
  // Also freeze wheel-zoom while the onboarding tutorial is open. The
  // overlay's scrim already blocks pointer events everywhere, so this only
  // matters if the cursor was already resting over the viewer (armed) when
  // the tutorial opened — no mouseleave fires until the next mouse move to
  // disarm it otherwise. useCoverageBetaHotkeys separately suppresses the
  // reusable viewer's *keyboard* zoom hotkeys the same way.
  const tutorialActive = useCoverageBetaStore((s) => s.tutorialActive);
  // Dismiss it whenever its anchor goes stale: the panel switching away (the
  // view stays mounted but invisible — a portal'd dropdown would float over
  // the shelf images), the tab/planogram changing under it, or a view reset
  // sliding the anchor away (the built-in popover dismisses on reset too).
  useEffect(() => {
    closePlanogramProductPopover();
  }, [
    isActiveView,
    activePlanogramTab,
    activePlanogramId,
    planogramResetNonce,
  ]);

  // --- Bridge: PlanogramViewer product clicks → active area's selection ---
  const productBoxInfo = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo,
  );
  const resetPlanoStore = useActivePlanoProductInfoStore((s) => s.reset);
  const lastConsumedRef = useRef("");
  // The store is a module-level singleton shared by every planogram flow: a
  // click left over by a previous task (e.g. a confirmation modal abandoned
  // mid-click) must not be consumed into THIS task's selection, so clear it on
  // mount. Declared before the consume effect so it runs first — and the
  // consume effect re-reads the store at fire time (not just its render
  // closure), which is what actually blocks the stale value on mount. Also
  // leave nothing behind ourselves: clear on unmount too (read-only mode
  // routes legacy-viewer clicks into the store without ever consuming them).
  useEffect(() => {
    resetPlanoStore();
    return () => resetPlanoStore();
  }, [resetPlanoStore]);

  useEffect(() => {
    if (!isActiveView) return;
    // Read-only (admin) view: clicking a planogram product must not mutate the
    // submitted selection.
    if (useCoverageBetaStore.getState().isReadOnly) return;
    // Re-read at fire time: the render closure can hold a value that a reset
    // (this view's own mount reset, or another owner's) has already cleared.
    const productId =
      useActivePlanoProductInfoStore.getState().productBoxInfo.product_id;
    if (!productId || productId === lastConsumedRef.current) return;
    lastConsumedRef.current = productId;

    (async () => {
      let product;
      try {
        product = await queryClient.fetchQuery(
          mvpApiGetProductOptions({ path: { product_id: productId } }),
        );
      } catch {
        resetPlanoStore();
        lastConsumedRef.current = "";
        return;
      }
      if (!product) {
        resetPlanoStore();
        lastConsumedRef.current = "";
        return;
      }

      // Read fresh state after the await.
      const {
        idxToFeEmptyArea,
        setIdxToFeEmptyArea,
        activeEmptyAreaIdx: areaIdx,
        activePlanogramTab: tab,
        assignBoxToSlot,
        confirming,
        setConfirming,
        nudgeConfirm,
      } = useCoverageBetaStore.getState();
      const planogramId = activePlanogramIdRef.current;

      // While a confirmation is pending, only the product being confirmed is
      // interactive here too: clicking it unselects and cancels the confirm;
      // any other product is blocked (mirrors the image behaviour).
      let cancelsConfirm = false;
      if (confirming != null) {
        const confirmingProductId =
          (
            referencesRef.current?.[confirming.referenceImgIdx] as
              | { product_ids?: (string | null)[] }
              | undefined
          )?.product_ids?.[confirming.referenceProductDetectionIdx] ?? "";
        if (productId !== confirmingProductId) {
          // Blocked: only the product being confirmed is interactive. Nudge the
          // confirm panel so the ignored click is explained.
          nudgeConfirm();
          resetPlanoStore();
          lastConsumedRef.current = "";
          return;
        }
        cancelsConfirm = true;
      }

      setIdxToFeEmptyArea(
        produce(idxToFeEmptyArea, (draft) => {
          const area = draft[areaIdx];
          if (!area || area.isDeleted) return;
          // Toggle by product_id regardless of how it was picked, so a product
          // selected on the image can be unselected here (and vice-versa).
          const isSelected = area.selectedProducts.some(
            (p) => p.id === productId,
          );
          if (isSelected) {
            area.selectedProducts = area.selectedProducts.filter(
              (p) => p.id !== productId,
            );
            if (area.selectedProducts.length === 0) area.isValid = false;
            // Removing a product voids any prior confirmation for this area.
            area.confirmedProductId = null;
          } else {
            area.selectedProducts.push({
              ...product,
              source: "planogram",
              planogram_id: planogramId,
            });
            area.isValid = true;
            // A product-bearing (confirmed) area must be visible.
            area.isHidden = false;
          }
        }),
      );
      // Bind the box to the planogram it was picked from.
      assignBoxToSlot(areaIdx, tab);
      // Unselecting the product being confirmed cancels the confirm step.
      if (cancelsConfirm) setConfirming(null);
      resetPlanoStore();
      lastConsumedRef.current = "";
    })();
  }, [productBoxInfo.product_id, isActiveView, queryClient, resetPlanoStore]);

  if (planogramSlots.length === 0) {
    return (
      <Center className="h-full">
        <Text size="sm" c="dimmed">
          No planogram available for this task
        </Text>
      </Center>
    );
  }

  return (
    <div
      className="h-full flex flex-col"
      onMouseEnter={() => setHoveredView("planogram")}
      data-cb-tour="planogram-view"
    >
      <div className="flex items-center gap-1 p-2 border-b flex-wrap">
        {slotLabels.map((slot, i) => (
          <button
            key={`${slot.planogramId}-${i}`}
            onClick={() => {
              setActivePlanogramTab(i);
              useCoverageBetaStore.getState().recordEvent("plano_tab_switch");
            }}
            className={cn(
              "px-2 py-1 rounded text-xs border max-w-[140px] truncate",
              i === activePlanogramTab
                ? "bg-blue-50 border-blue-300 text-blue-700 font-medium"
                : "border-gray-200 text-gray-600 hover:bg-gray-50",
            )}
            title={slot.label}
          >
            {i + 1}. {slot.label}
          </button>
        ))}

        {/* Planogram editing (replace/add) is hidden in read-only (admin). */}
        {!isReadOnly && (
          <div className="ml-auto flex items-center gap-1">
            {!!activeSlot && (
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                leftSection={<IconReplace size={12} />}
                onClick={() => {
                  setModalPreselectedSlot(activePlanogramTab);
                  setModalOpen(true);
                }}
              >
                Replace
              </Button>
            )}
            {planogramSlots.length < MAX_PLANOGRAM_SLOTS && (
              <Button
                size="compact-xs"
                variant="light"
                leftSection={<IconPlus size={12} />}
                onClick={() => {
                  setModalPreselectedSlot(null);
                  setModalOpen(true);
                }}
              >
                Add
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0">
        {activePlanogramLoading || !isPlanogramReady ? (
          <Center className="h-full">
            <Loader />
          </Center>
        ) : activePlanogramId ? (
          <div className="h-full relative">
            {/* The focused product isn't on the shown planogram: say so (and
                offer a jump when it's on another loaded aisle) rather than
                leaving the labeler staring at a recentered planogram with no
                red box. */}
            {showFocusNotice && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 w-[90%] max-w-md pointer-events-none">
                {focusProductOnOtherTab ? (
                  <button
                    onClick={() => setActivePlanogramTab(focusProductSlot)}
                    className="pointer-events-auto w-full flex items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2.5 shadow-lg ring-2 ring-orange-300 cursor-pointer"
                  >
                    <IconMapPinOff size={18} />
                    <span>
                      Not here — product is on{" "}
                      <span className="underline underline-offset-2">
                        {slotLabels[focusProductSlot]?.label}
                      </span>
                      . Click to go.
                    </span>
                  </button>
                ) : (
                  <div className="w-full flex items-center justify-center gap-2 rounded-lg bg-orange-500 text-white text-sm font-semibold px-4 py-2.5 shadow-lg ring-2 ring-orange-300">
                    <IconMapPinOff size={18} />
                    <span>Selected product isn&apos;t on this planogram</span>
                  </div>
                )}
              </div>
            )}
            {/* The `!h-full` override exists only to stretch the legacy
                PlanogramViewer's hardcoded `h-[400px]` root. It lives on this
                wrapper rather than the container above because `[&>div]` hits
                EVERY direct div child: on the container it also caught the
                notice above, stretching that transparent absolutely-positioned
                banner into a full-height shield that swallowed every click and
                drag over the planogram — and, z-30 outranking the QA footer's
                z-10, over the QA panel too. */}
            <div className="h-full [&>div]:!h-full">
              {isEnableReusablePlanoViewer ? (
                <ReusablePlanogramViewer
                  ref={reusableViewerRef}
                  enableMinimap
                  enableZoom={!isPlanoPopoverOpen && !tutorialActive}
                  zoomHotkeys={REUSABLE_PLANO_ZOOM_HOTKEYS}
                  products={activePlanogramData?.products ?? []}
                  activeProductIds={reusableActiveProductIds}
                  backgroundImageUrl={activePlanogramData?.image_url ?? ""}
                >
                  {renderCoverageBetaPlanogramProduct}
                </ReusablePlanogramViewer>
              ) : (
                <PlanogramViewer
                  planogram_id={activePlanogramId}
                  fitContent
                  selectedProductIds={selectedProductIdsList}
                  highlightedProductIds={highlightedProductIds}
                  zoomToProductIds={zoomToProductIds}
                  pulseOnFrame
                  resetViewNonce={planogramResetNonce}
                  // Surface the viewer's zoom controller so the zoom hotkeys act
                  // on the planogram when the mouse is over it.
                  onTransformRef={setPlanogramPinchRef}
                />
              )}
            </div>
          </div>
        ) : (
          <Center className="h-full">
            <Text size="sm" c="dimmed">
              No planogram for this aisle
            </Text>
          </Center>
        )}
      </div>

      <PlanogramSearchModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={(slots) => {
          applyPlanogramModalEdits(slots);
          setModalOpen(false);
        }}
        currentSlots={slotLabels}
        preselectedSlot={modalPreselectedSlot}
        retailerId={retailerId}
        storeId={storeId}
      />
    </div>
  );
};
